import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { Breadcrumb, type BreadcrumbItem } from '@/components/shared/Breadcrumb';
import { SectionLabel } from '@/components/ui/section-label';
import { cn } from '@/lib/utils';

export type PageHeaderProps = {
  breadcrumb?: BreadcrumbItem[];
  /** Small section label above the title (icon + text). */
  label?: { icon?: LucideIcon; text: ReactNode };
  title: ReactNode;
  /** Monospace title for IDs, references and codes. */
  mono?: boolean;
  description?: ReactNode;
  /** Buttons on the right (stacked under the title on phones). */
  actions?: ReactNode;
  /** Extra content under the description (filters, summary). */
  children?: ReactNode;
  className?: string;
};

/** The page identity zone every page starts with (breadcrumb, title, description, actions). */
export function PageHeader({
  breadcrumb,
  label,
  title,
  mono = false,
  description,
  actions,
  children,
  className,
}: PageHeaderProps) {
  return (
    <header
      className={cn(
        'flex flex-col gap-3 rounded-2xl border bg-card/80 px-6 py-5 shadow-sm backdrop-blur-sm',
        className
      )}
    >
      {breadcrumb && breadcrumb.length > 0 && <Breadcrumb items={breadcrumb} />}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex min-w-0 flex-col gap-1">
          {label && (
            <SectionLabel icon={label.icon} className="mb-1">
              {label.text}
            </SectionLabel>
          )}
          <h1 className={cn('page-title break-words', mono && 'font-mono')}>{title}</h1>
          {description && <p className="text-sm text-muted-foreground">{description}</p>}
        </div>
        {actions && <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>}
      </div>
      {children}
    </header>
  );
}
